/* eslint-disable @typescript-eslint/no-magic-numbers */
import Color from 'color';

import { ThemeBorderRadiusValues, ThemeColorVariableValues, ThemeDropShadowValues } from '@core/theme/types';

import {
  ThemeBorderRadiusesStoreModel,
  ThemeColorsStoreModel,
  ThemeDropShadowsStoreModel,
  ThemeFiltersStoreModel,
} from '@domain/theme/theme.models';

export const mapThemeColorsStoreModelToThemeColorVariableValues = (themeColors: ThemeColorsStoreModel): ThemeColorVariableValues => {
  return {
    '--color-custom-base-load': themeColors.customBaseLoad,
    '--color-custom-border-muted': themeColors.customBorderMuted,
    '--color-custom-asset-overview-poc-circle': themeColors.customAssetOverviewPocCircle,
    '--color-custom-asset-overview-dot-fill': themeColors.customAssetOverviewDotFill,
    '--color-custom-secondary-border': themeColors.customSecondaryBorder,
    '--color-custom-success': themeColors.customSuccess,
    '--color-custom-error': themeColors.customError,
    '--color-custom-ready': themeColors.customReady,
    '--color-custom-charging': themeColors.customCharging,
    '--color-custom-queued': themeColors.customQueued,
    '--color-custom-faulted': themeColors.customFaulted,
    '--color-custom-soc-doughnut-dash': Color(themeColors.strokePrimary).alpha(0.24).string(),
    '--color-custom-timeline-scrollbar-track': themeColors.customTimelineScrollbarTrack,
    '--color-custom-disabled-input-background': themeColors.customDisabledInputBackground,
    '--color-custom-disabled-input-color': themeColors.customDisabledInputColor,
    '--color-custom-net-profit': themeColors.customNetProfit,
    '--color-custom-net-profit-disabled': themeColors.customNetProfitDisabled,
    '--color-custom-flex-drill-down-date-highlight': themeColors.customFlexDrillDownDateHighlight,
    '--color-custom-trading-table-edited-cell': themeColors.customTradingTableEditedCell,
    '--color-custom-trading-table-selected-overlay': themeColors.customTradingTableSelectedOverlay,

    '--color-background-main': themeColors.backgroundMain,
    '--color-background-fixed': themeColors.backgroundFixed,
    '--color-background-moveable': themeColors.backgroundMoveable,
    '--color-background-menu': themeColors.backgroundMenu,
    '--color-background-panel': themeColors.backgroundPanel,
    '--color-background-container-primary': themeColors.backgroundContainerPrimary,
    '--color-background-container-primary-50': Color(themeColors.backgroundContainerPrimary).alpha(0.5).string(),
    '--color-background-container-secondary': themeColors.backgroundContainerSecondary,
    '--color-background-container-inverse': themeColors.backgroundContainerInverse,
    '--color-background-container-tertiary': themeColors.backgroundContainerTertiary,
    '--color-background-border': themeColors.backgroundBorder,

    '--color-foreground-primary-100': themeColors.foregroundPrimary,
    '--color-foreground-primary-80': Color(themeColors.foregroundPrimary).alpha(0.8).string(),
    '--color-foreground-primary-60': Color(themeColors.foregroundPrimary).alpha(0.6).string(),
    '--color-foreground-primary-40': Color(themeColors.foregroundPrimary).alpha(0.4).string(),
    '--color-foreground-primary-20': Color(themeColors.foregroundPrimary).alpha(0.2).string(),
    '--color-foreground-primary-12': Color(themeColors.foregroundPrimary).alpha(0.12).string(),
    '--color-foreground-primary-8': Color(themeColors.foregroundPrimary).alpha(0.08).string(),

    '--color-foreground-secondary-100': themeColors.foregroundSecondary,
    '--color-foreground-secondary-80': Color(themeColors.foregroundSecondary).alpha(0.8).string(),
    '--color-foreground-secondary-60': Color(themeColors.foregroundSecondary).alpha(0.6).string(),
    '--color-foreground-secondary-40': Color(themeColors.foregroundSecondary).alpha(0.4).string(),
    '--color-foreground-secondary-20': Color(themeColors.foregroundSecondary).alpha(0.2).string(),
    '--color-foreground-secondary-12': Color(themeColors.foregroundSecondary).alpha(0.12).string(),
    '--color-foreground-secondary-8': Color(themeColors.foregroundSecondary).alpha(0.08).string(),

    '--color-action-primary': themeColors.actionPrimary,
    '--color-action-primary-30': Color(themeColors.actionPrimary).alpha(0.3).string(),
    '--color-action-primary-20': Color(themeColors.actionPrimary).alpha(0.2).string(),
    '--color-action-primary-text': themeColors.actionPrimaryText,
    '--color-action-secondary': themeColors.actionSecondary,
    '--color-action-secondary-30': Color(themeColors.actionSecondary).alpha(0.3).string(),
    '--color-action-secondary-18': Color(themeColors.actionSecondary).alpha(0.18).string(),
    '--color-action-secondary-text': themeColors.actionSecondaryText,

    '--color-chargingstatus-foreground': '#FFFFFF',
    '--color-chargingstatus-ready-100': themeColors.chargingStatusReady,
    '--color-chargingstatus-ready-80': Color(themeColors.chargingStatusReady).alpha(0.8).string(),
    '--color-chargingstatus-ready-30': Color(themeColors.chargingStatusReady).alpha(0.3).string(),
    '--color-chargingstatus-charging-100': themeColors.chargingStatusCharging,
    '--color-chargingstatus-charging-80': Color(themeColors.chargingStatusCharging).alpha(0.8).string(),
    '--color-chargingstatus-charging-30': Color(themeColors.chargingStatusCharging).alpha(0.3).string(),
    '--color-chargingstatus-queued-100': themeColors.chargingStatusQueued,
    '--color-chargingstatus-queued-80': Color(themeColors.chargingStatusQueued).alpha(0.8).string(),
    '--color-chargingstatus-queued-30': Color(themeColors.chargingStatusQueued).alpha(0.3).string(),
    '--color-chargingstatus-faulted-100': themeColors.chargingStatusFaulted,
    '--color-chargingstatus-faulted-80': Color(themeColors.chargingStatusFaulted).alpha(0.8).string(),
    '--color-chargingstatus-faulted-30': Color(themeColors.chargingStatusFaulted).alpha(0.3).string(),
    '--color-chargingstatus-indepot-100': themeColors.chargingStatusInDepot,
    '--color-chargingstatus-indepot-80': Color(themeColors.chargingStatusInDepot).alpha(0.8).string(),
    '--color-chargingstatus-indepot-30': Color(themeColors.chargingStatusInDepot).alpha(0.3).string(),
    '--color-chargingstatus-onroad-100': themeColors.chargingStatusOnRoad,
    '--color-chargingstatus-onroad-80': Color(themeColors.chargingStatusOnRoad).alpha(0.8).string(),
    '--color-chargingstatus-onroad-30': Color(themeColors.chargingStatusOnRoad).alpha(0.3).string(),
    '--color-chargingstatus-unknown-100': themeColors.chargingStatusUnknown,
    '--color-chargingstatus-unknown-80': Color(themeColors.chargingStatusUnknown).alpha(0.8).string(),
    '--color-chargingstatus-unknown-30': Color(themeColors.chargingStatusUnknown).alpha(0.3).string(),
    '--color-custom-disabled-card': themeColors.customDisabledCard,
    '--color-custom-disabled-card-text': themeColors.customDisabledCardText,

    '--color-session-outcome-completed': themeColors.sessionOutcomeCompleted,
    '--color-session-outcome-short': themeColors.sessionOutcomeShort,
    '--color-session-outcome-incomplete': themeColors.sessionOutcomeIncomplete,
    '--color-session-outcome-short-and-incomplete': themeColors.sessionOutcomeShortAndIncomplete,
    '--color-session-outcome-faulted': themeColors.sessionOutcomeFaulted,
    '--color-session-outcome-short-and-faulted': themeColors.sessionOutcomeShortAndFaulted,
    '--color-vehicle-telemetry-never-reported': themeColors.vehicleTelemetryNeverReported,
    '--color-vehicle-telemetry-offline-3-plus-days': themeColors.vehicleTelemetryOfflineThreeDays,
    '--color-vehicle-telemetry-offline-1-to-2-days': themeColors.vehicleTelemetryOfflineOneToTwoDays,
    '--color-vehicle-telemetry-offline-over-12-hours': themeColors.vehicleTelemetryOfflineTwelveHours,
    '--color-vehicle-telemetry-intermittent': themeColors.vehicleTelemetryIntermittent,

    '--color-chart-ev': themeColors.chartEv,
    '--color-chart-baseload': themeColors.chartDepotLoad,
    '--color-chart-pv-solar': themeColors.chartPvSolar,
    '--color-chart-grid': themeColors.chartGrid,
    '--color-chart-battery': themeColors.chartBattery,
    '--color-chart-physical-notification': themeColors.chartPhysicalNotification,
    '--color-chart-gross-revenue': themeColors.chartGrossRevenue,
    '--color-chart-net-revenue': themeColors.chartNetRevenue,

    '--color-header-title': themeColors.headerTitle,
    '--color-header-breadcrumb-current': themeColors.headerBreadcrumbCurrent,
    '--color-header-breadcrumb-past': Color(themeColors.headerBreadcrumbCurrent).alpha(0.6).string(),
    '--color-header-tab-text': themeColors.headerTabText,
    '--color-header-tab-text-80': Color(themeColors.headerTabText).alpha(0.8).string(),
    '--color-header-tab-text-30': Color(themeColors.headerTabText).alpha(0.3).string(),
    '--color-header-tab-text-22': Color(themeColors.headerTabText).alpha(0.22).string(),
    '--color-header-tab-line': themeColors.headerTabLine,
    '--color-header-actionitem-bg': themeColors.headerActionItemBg,
    '--color-header-actionitem-bg-50': Color(themeColors.headerActionItemBg).alpha(0.5).string(),
    '--color-header-actionitem-fg': themeColors.headerActionItemFg,

    '--color-status-success': themeColors.statusSuccess,
    '--color-status-success-30': Color(themeColors.statusSuccess).alpha(0.3).string(),
    '--color-status-success-text': themeColors.statusSuccessText,
    '--color-status-error': themeColors.statusError,
    '--color-status-error-30': Color(themeColors.statusError).alpha(0.3).string(),
    '--color-status-error-20': Color(themeColors.statusError).alpha(0.2).string(),
    '--color-status-error-0': Color(themeColors.statusError).alpha(0).string(),
    '--color-status-error-text': themeColors.statusErrorText,
    '--color-status-warning': themeColors.statusWarning,
    '--color-status-warning-30': Color(themeColors.statusWarning).alpha(0.3).string(),
    '--color-status-warning-text': themeColors.statusWarningText,
    '--color-status-info': themeColors.statusInfo,
    '--color-status-info-30': Color(themeColors.statusInfo).alpha(0.3).string(),
    '--color-status-info-text': themeColors.statusInfoText,
    '--color-status-idle': themeColors.statusIdle,
    '--color-status-offline': themeColors.statusOffline,
    '--color-status-offline-30': Color(themeColors.statusOffline).alpha(0.3).string(),
    '--color-status-offline-text': themeColors.statusOfflineText,

    '--color-risk-band-low': themeColors.riskBandLow,
    '--color-risk-band-low-20': Color(themeColors.riskBandLow).alpha(0.2).string(),
    '--color-risk-band-moderate': themeColors.riskBandModerate,
    '--color-risk-band-moderate-20': Color(themeColors.riskBandModerate).alpha(0.2).string(),
    '--color-risk-band-high': themeColors.riskBandHigh,
    '--color-risk-band-high-20': Color(themeColors.riskBandHigh).alpha(0.2).string(),
    '--color-risk-band-not-enough-data': themeColors.riskBandNotEnoughData,
    '--color-risk-band-not-enough-data-20': Color(themeColors.riskBandNotEnoughData).alpha(0.2).string(),
    '--color-risk-band-not-operational': themeColors.riskBandNotOperational,
    '--color-risk-band-not-operational-20': Color(themeColors.riskBandNotOperational).alpha(0.2).string(),

    '--color-trip-health-good': themeColors.tripHealthGood,
    '--color-trip-health-warning': themeColors.tripHealthWarning,
    '--color-trip-health-error': themeColors.tripHealthError,

    '--color-stroke-primary': themeColors.strokePrimary,
    '--color-stroke-primary-8': Color(themeColors.strokePrimary).alpha(0.08).string(),
    '--color-stroke-card-border': themeColors.strokeCardBorder,
    '--color-stroke-container': themeColors.strokeContainer,
    '--color-stroke-action': themeColors.strokeAction,
    '--color-stroke-tertiary': themeColors.strokeTertiary,
    '--color-stroke-tertiary-16': Color(themeColors.strokeTertiary).alpha(0.16).string(),
  };
};

export const mapThemeBorderRadiusesStoreModelToThemeBorderRadiusValues = (
  themeBorderRadiuses: ThemeBorderRadiusesStoreModel
): ThemeBorderRadiusValues => {
  return {
    '--size-radius-cards': themeBorderRadiuses.cards,
    '--size-radius-menu': themeBorderRadiuses.menu,
    '--size-radius-container': themeBorderRadiuses.container,
    '--size-radius-container-secondary': themeBorderRadiuses.containerSecondary,
    '--size-radius-cta': themeBorderRadiuses.cta,
    '--size-radius-circle': themeBorderRadiuses.circle,
    '--size-radius-primary': themeBorderRadiuses.primary,
    '--size-radius-secondary': themeBorderRadiuses.secondary,
    '--size-radius-tertiary': themeBorderRadiuses.tertiary,
  };
};

export const mapThemeDropShadowsStoreModelToThemeDropShadowValues = (
  themeDropShadows: ThemeDropShadowsStoreModel,
  themeColors: ThemeColorsStoreModel
): ThemeDropShadowValues => {
  return {
    '--drop-shadow-custom-base-load': themeDropShadows.customBaseLoad,
    '--drop-shadow-custom-card-fixed': themeDropShadows.customCardFixed,
    '--drop-shadow-dropdown': themeDropShadows.dropdown,

    '--drop-shadow-chargingstatus-charging': `0 0 10px ${Color(themeColors.chargingStatusCharging).alpha(0.6).string()}`,
    '--drop-shadow-chargingstatus-queued': `0 0 10px ${Color(themeColors.chargingStatusQueued).alpha(0.6).string()}`,
    '--drop-shadow-foreground-primary-100': `0 0 10px ${Color(themeColors.chargingStatusOnRoad).alpha(0.6).string()}`,
    '--drop-shadow-chargingstatus-ready': `0 0 10px ${Color(themeColors.chargingStatusReady).alpha(0.6).string()}`,
  };
};

export const mapThemeFitlersStoreModelToFilterValues = (filters: ThemeFiltersStoreModel): Record<string, string> => {
  return {
    '--filter-custom-card-moveable': filters.customCardMoveable,
  };
};
